import { useEffect, useMemo, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { supabase } from '../lib/supabase';
import { requestLocationPermission, startSharingLocation, stopSharingLocation } from '../lib/location';
import { useActiveGroup } from '../state/groups';
import { useGroupCrew } from '../state/session';
import { usePlaces } from '../shared/places';
import { useIncomingMessages } from '../lib/messages';
import { GroupPicker } from './GroupPicker';
import { PTTButton } from '../components/PTTButton';
import { OutboxPill } from '../components/OutboxPill';
import { WhisperStatus } from '../components/WhisperStatus';
import { RollCallOverlay } from '../components/RollCallOverlay';
import { useActiveRollCall } from '../lib/rollcall';
import { MessagesScreen } from './MessagesScreen';
import { NotificationsToggle } from '../components/NotificationsToggle';
import { CrewLegend } from '../components/CrewLegend';
import { AudioUnlockBanner } from '../components/AudioUnlockBanner';
import { joinGroupByCode } from '../shared/groups';
import { isGroupMuted, onGroupMuteChange, setGroupMute } from '../lib/groupMute';
import { useGroupTalking } from '../lib/talking';
import { crewColor, SELF_COLOR } from '../lib/crewColor';
import { colors } from '../theme';

type LocRow = {
  user_id: string;
  lat: number;
  lng: number;
  updated_at: string;
  status: string | null;
};

type PresenceState = 'fresh' | 'stale' | 'offline' | 'no_signal' | 'sharing_off';

const STALE_MS = 10 * 60 * 1000;

function presenceFor(
  loc: LocRow | undefined,
  share_location: boolean
): PresenceState {
  if (!loc) return share_location ? 'no_signal' : 'sharing_off';
  if (loc.status === 'offline') return 'offline';
  const ageMs = Date.now() - new Date(loc.updated_at).getTime();
  return ageMs > STALE_MS ? 'stale' : 'fresh';
}

function stalenessLabel(iso: string): string {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 30) return 'now';
  if (secs < 90) return '1m ago';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

// Small CSS-based DivIcon factory so we don't ship PNG pin assets. Color maps
// to presence state, matching the native MapScreen's amber/grey/dim scheme.
// Name label floats above the dot so you can scan crew positions at a glance.
function pinIcon(color: string, name: string, isSelf = false): L.DivIcon {
  const safeName = name.replace(/[<>&"']/g, (c) => `&#${c.charCodeAt(0)};`);
  return L.divIcon({
    className: 'hive-pin',
    iconSize: [120, 44],
    iconAnchor: [60, 33],
    html: `
      <div style="position: relative; width: 120px; height: 44px; pointer-events: none;">
        <div style="
          position: absolute;
          bottom: 22px; left: 50%; transform: translateX(-50%);
          background: rgba(23,22,28,0.9);
          color: #ece5d3;
          font: 700 11px/1 system-ui, -apple-system, sans-serif;
          padding: 3px 6px;
          border-radius: 4px;
          border: 1px solid #2f2d37;
          white-space: nowrap;
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
          pointer-events: auto;
        ">${safeName}${isSelf ? ' (you)' : ''}</div>
        <div style="
          position: absolute;
          bottom: 0; left: 50%; transform: translateX(-50%);
          width: 22px; height: 22px; border-radius: 50%;
          background: ${color};
          border: 3px solid ${isSelf ? '#ece5d3' : '#17161c'};
          box-shadow: 0 2px 6px rgba(0,0,0,0.6);
          pointer-events: auto;
        "></div>
      </div>
    `,
  });
}

const placeIcon = L.divIcon({
  className: 'hive-place',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  html: `<div style="
    width: 16px; height: 16px; border-radius: 3px;
    background: #3a7bd5;
    border: 2px solid #17161c;
    transform: rotate(45deg);
  "></div>`,
});

function MapAutoCenter({ self }: { self: { lat: number; lng: number } | null }) {
  const map = useMap();
  const centered = useRef(false);
  useEffect(() => {
    if (!self || centered.current) return;
    centered.current = true;
    map.flyTo([self.lat, self.lng], 14, { duration: 0.8 });
  }, [self, map]);
  return null;
}

export function MapScreen({ session }: { session: Session }) {
  const { groups, active, switchTo, refresh, loading: groupsLoading } = useActiveGroup(session.user.id);
  const groupId = active?.id ?? null;
  const { crew } = useGroupCrew(groupId);
  const { places } = usePlaces(groupId);
  const { messages, unreadCount, markAllRead } = useIncomingMessages(session.user.id, groupId);
  const { rollCall, responses: rollCallResponses } = useActiveRollCall(groupId);
  const talkingIds = useGroupTalking(groupId);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [locs, setLocs] = useState<Record<string, LocRow>>({});
  const [, setTick] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [permError, setPermError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [mapLayer, setMapLayer] = useState<'street' | 'satellite'>(
    () => (localStorage.getItem('hive.mapLayer.v1') as 'street' | 'satellite') || 'street'
  );
  useEffect(() => {
    localStorage.setItem('hive.mapLayer.v1', mapLayer);
  }, [mapLayer]);
  const [muted, setMuted] = useState<boolean>(() => (groupId ? isGroupMuted(groupId) : false));
  useEffect(() => {
    if (!groupId) {
      setMuted(false);
      return;
    }
    return onGroupMuteChange(() => setMuted(isGroupMuted(groupId)));
  }, [groupId]);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(id);
  }, [toast]);

  // If the URL has ?join=CODE (shared invite link), auto-join that group
  // once and strip the param so reloads don't retry. Errors are toasted but
  // non-fatal — "already a member" is a benign case.
  useEffect(() => {
    const url = new URL(window.location.href);
    const joinCode = url.searchParams.get('join');
    if (!joinCode) return;
    url.searchParams.delete('join');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    (async () => {
      try {
        const id = await joinGroupByCode(joinCode);
        await refresh();
        switchTo(id);
        setToast(`Joined group.`);
      } catch (e: any) {
        setToast(`Couldn't join: ${e?.message ?? String(e)}`);
      }
    })();
  }, [refresh, switchTo]);

  // Ask for location + start sharing on mount. Foreground-only; no cleanup
  // needed beyond stopping the watch when the component unmounts.
  useEffect(() => {
    (async () => {
      const perm = await requestLocationPermission();
      if (perm === 'denied') {
        setPermError('Location permission denied. Pins won\'t appear until you allow it in browser settings.');
        return;
      }
      if (perm === 'unavailable') {
        setPermError('This browser does not support location.');
        return;
      }
      startSharingLocation(session.user.id, (e) => console.warn('location upsert failed', e));
    })();
    return () => stopSharingLocation();
  }, [session.user.id]);

  // Initial load of every visible user_locations row (RLS gates by shared groups).
  useEffect(() => {
    if (!groupId) return;
    (async () => {
      const { data, error } = await supabase.from('user_locations').select('*');
      if (error) {
        console.warn('locations load failed', error);
        return;
      }
      const map: Record<string, LocRow> = {};
      for (const row of data as LocRow[]) map[row.user_id] = row;
      setLocs(map);
    })();
  }, [groupId]);

  // Realtime subscription on user_locations.
  useEffect(() => {
    const channel = supabase
      .channel('user-locations-feed')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'user_locations' },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            const old = payload.old as Partial<LocRow>;
            const id = old?.user_id;
            if (!id) return;
            setLocs((prev) => {
              if (!prev[id]) return prev;
              const next = { ...prev };
              delete next[id];
              return next;
            });
            return;
          }
          const row = payload.new as LocRow;
          if (!row?.user_id) return;
          setLocs((prev) => ({ ...prev, [row.user_id]: row }));
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Tick for staleness labels.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const self = locs[session.user.id];
  const selfLatLng = self ? { lat: self.lat, lng: self.lng } : null;

  const pins = useMemo(() => {
    if (!crew) return [];
    return crew
      .map((c) => {
        const loc = locs[c.id];
        if (!loc) return null;
        const presence = presenceFor(loc, c.share_location);
        return { crew: c, loc, presence };
      })
      .filter(
        (p): p is { crew: NonNullable<typeof p>['crew']; loc: LocRow; presence: PresenceState } =>
          p !== null
      );
  }, [crew, locs]);

  if (groupsLoading) {
    return <div className="map-loading">Loading…</div>;
  }

  if (!active) {
    return (
      <div className="map-empty">
        <h2>No groups yet</h2>
        <p>Create a group or join one with a code.</p>
        <GroupPicker
          groups={groups ?? []}
          activeId={null}
          onSelect={switchTo}
          onChanged={refresh}
        />
        <button onClick={() => supabase.auth.signOut()} className="signout">
          Sign out
        </button>
        <style>{`
          .map-empty {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 24px;
            gap: 8px;
          }
          .map-empty h2 { color: ${colors.text}; margin: 0; }
          .map-empty p { color: ${colors.textMuted}; margin: 0 0 16px; }
          .signout { background: transparent; color: ${colors.alt}; border: none; margin-top: 24px; font-size: 14px; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="map-wrap">
      <MapContainer
        center={[39.8283, -98.5795]}
        zoom={4}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
      >
        {mapLayer === 'satellite' ? (
          <TileLayer
            key="sat"
            attribution='Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19}
          />
        ) : (
          <TileLayer
            key="osm"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        )}
        <MapAutoCenter self={selfLatLng} />
        {pins.map(({ crew: c, loc, presence }) => {
          const isSelf = c.id === session.user.id;
          const color = isSelf
            ? SELF_COLOR
            : presence === 'offline'
            ? colors.accentDim
            : presence === 'stale'
            ? '#7a7a7a'
            : crewColor(c.id);
          return (
            <Marker key={c.id} position={[loc.lat, loc.lng]} icon={pinIcon(color, c.display_name, isSelf)}>
              <Popup>
                <strong>{c.display_name}</strong>
                <br />
                {presence === 'offline' ? 'offline · ' : ''}
                {stalenessLabel(loc.updated_at)}
              </Popup>
            </Marker>
          );
        })}
        {places.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={placeIcon}>
            <Popup>
              <strong>{p.name}</strong>
              <br />
              Saved place
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <header className="map-header">
        <button onClick={() => setPickerOpen(true)} className="group-chip">
          <span className="group-name">{active.name}</span>
          <span className="group-chevron">⌄</span>
        </button>
        <div className="header-right">
          <button
            onClick={() => groupId && setGroupMute(groupId, !muted)}
            className="mute-chip"
            aria-label={muted ? 'Unmute group' : 'Mute group'}
            title={muted ? 'Group muted — tap to unmute' : 'Mute group'}
            disabled={!groupId}
          >
            {muted ? '🔕' : '🔔'}
          </button>
          <button
            onClick={() => {
              setMessagesOpen(true);
              markAllRead();
            }}
            className="msg-chip"
          >
            Messages
            {unreadCount > 0 && <span className="msg-badge">{unreadCount}</span>}
          </button>
          <button onClick={() => supabase.auth.signOut()} className="signout-chip">
            Sign out
          </button>
        </div>
      </header>

      {permError && <div className="perm-banner">{permError}</div>}

      <CrewLegend crew={crew ?? []} locs={locs} selfId={session.user.id} talkingIds={talkingIds} />

      <button
        className="layer-toggle"
        onClick={() => setMapLayer(mapLayer === 'street' ? 'satellite' : 'street')}
        aria-label={mapLayer === 'street' ? 'Switch to satellite' : 'Switch to street map'}
        title={mapLayer === 'street' ? 'Satellite view' : 'Street view'}
      >
        {mapLayer === 'street' ? '🛰' : '🗺'}
      </button>

      <AudioUnlockBanner
        latestPlayablePath={
          messages.find(
            (m) =>
              m.sender_id !== session.user.id &&
              (m.recipient_id === null || m.recipient_id === session.user.id) &&
              !!m.audio_path
          )?.audio_path ?? null
        }
      />

      {messages[0] && (
        <div
          className={`latest ${messages[0].urgency === 'urgent' ? 'latest-urgent' : ''} ${
            messages[0].recipient_id ? 'latest-direct' : ''
          }`}
        >
          <div className="latest-from">
            {messages[0].urgency === 'urgent' ? '🚨 URGENT · ' : ''}
            {crew?.find((c) => c.id === messages[0].sender_id)?.display_name ?? 'Someone'}
          </div>
          <div className="latest-body">
            {messages[0].transcript ?? '🎤 voice message'}
          </div>
          {unreadCount > 0 && (
            <button className="latest-read" onClick={markAllRead}>
              Mark read ({unreadCount})
            </button>
          )}
        </div>
      )}

      <OutboxPill />
      <WhisperStatus />
      <RollCallOverlay
        rollCall={rollCall}
        responses={rollCallResponses}
        crew={crew ?? []}
        selfId={session.user.id}
      />
      <MessagesScreen
        visible={messagesOpen}
        onClose={() => setMessagesOpen(false)}
        messages={messages}
        crew={crew ?? []}
        selfId={session.user.id}
        groupId={groupId}
        onWhisperBack={(senderId) => {
          setReplyTo(senderId);
          setMessagesOpen(false);
        }}
      />

      {replyTo && (
        <button className="reply-banner" onClick={() => setReplyTo(null)}>
          Whispering back to{' '}
          <strong>
            {crew?.find((c) => c.id === replyTo)?.display_name ?? 'someone'}
          </strong>
          {' · '}tap to cancel
        </button>
      )}

      <PTTButton
        senderId={session.user.id}
        groupId={active.id}
        crew={crew ?? []}
        places={places}
        selfLocation={selfLatLng}
        otherLocations={locs}
        onCommandResult={setToast}
        disabled={pickerOpen}
        replyToId={replyTo}
        onReplySent={() => setReplyTo(null)}
      />

      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}

      {pickerOpen && (
        <div className="picker-sheet" onClick={(e) => e.target === e.currentTarget && setPickerOpen(false)}>
          <div className="picker-inner">
            <div className="picker-handle" />
            <GroupPicker
              groups={groups ?? []}
              activeId={active.id}
              onSelect={(id) => {
                switchTo(id);
                setPickerOpen(false);
              }}
              onChanged={refresh}
            />
            <NotificationsToggle userId={session.user.id} />
          </div>
        </div>
      )}

      <style>{`
        .map-wrap { position: fixed; inset: 0; }
        .map-loading {
          min-height: 100vh;
          display: flex; align-items: center; justify-content: center;
          color: ${colors.textMuted};
        }
        .map-header {
          position: absolute;
          top: calc(env(safe-area-inset-top) + 12px);
          left: 12px;
          right: 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          z-index: 1000;
        }
        .group-chip {
          background: rgba(23,22,28,0.92);
          color: ${colors.text};
          border: 1px solid ${colors.divider};
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 15px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .group-chevron { color: ${colors.accent}; font-size: 14px; }
        .signout-chip {
          background: rgba(23,22,28,0.92);
          color: ${colors.alt};
          border: 1px solid ${colors.divider};
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 13px;
        }
        .header-right { display: flex; gap: 8px; align-items: center; }
        .mute-chip {
          background: rgba(23,22,28,0.92);
          color: ${colors.accent};
          border: 1px solid ${colors.divider};
          border-radius: 10px;
          padding: 10px 12px;
          font-size: 16px;
          line-height: 1;
          cursor: pointer;
        }
        .mute-chip:disabled { opacity: 0.5; cursor: default; }
        .layer-toggle {
          position: absolute;
          right: 12px;
          bottom: calc(env(safe-area-inset-bottom) + 170px);
          width: 44px;
          height: 44px;
          border-radius: 22px;
          border: 1px solid ${colors.divider};
          background: rgba(23,22,28,0.92);
          color: ${colors.text};
          font-size: 18px;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          cursor: pointer;
          box-shadow: 0 4px 10px rgba(0,0,0,0.4);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
        }
        .layer-toggle:active { transform: scale(0.94); }
        .msg-chip {
          background: rgba(23,22,28,0.92);
          color: ${colors.accent};
          border: 1px solid ${colors.divider};
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 13px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .msg-badge {
          background: ${colors.danger};
          color: ${colors.bg};
          border-radius: 999px;
          min-width: 18px;
          height: 18px;
          padding: 0 5px;
          font-size: 11px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .perm-banner {
          position: absolute;
          top: calc(env(safe-area-inset-top) + 72px);
          left: 12px; right: 12px;
          background: ${colors.danger};
          color: ${colors.bg};
          padding: 10px 14px;
          border-radius: 8px;
          font-size: 13px;
          z-index: 1000;
        }
        .preview-banner {
          position: absolute;
          bottom: calc(env(safe-area-inset-bottom) + 12px);
          left: 12px; right: 12px;
          background: rgba(23,22,28,0.92);
          color: ${colors.textMuted};
          padding: 10px;
          border-radius: 8px;
          text-align: center;
          font-size: 12px;
          z-index: 1000;
          border: 1px solid ${colors.divider};
        }
        .picker-sheet {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.6);
          z-index: 2000;
          display: flex;
          align-items: flex-end;
        }
        .picker-inner {
          width: 100%;
          background: ${colors.bg};
          border-radius: 16px 16px 0 0;
          padding: 8px 0 24px;
          max-height: 80vh;
          overflow-y: auto;
        }
        .picker-handle {
          width: 40px;
          height: 4px;
          background: ${colors.divider};
          border-radius: 2px;
          margin: 8px auto 4px;
        }
        .latest {
          position: absolute;
          top: calc(env(safe-area-inset-top) + 72px);
          left: 254px; right: 12px;
          padding: 10px 14px;
          background: ${colors.accent};
          color: ${colors.bg};
          border-radius: 10px;
          z-index: 1000;
        }
        @media (max-width: 520px) {
          .latest {
            top: auto;
            bottom: calc(env(safe-area-inset-bottom) + 180px);
            left: 12px;
            right: 12px;
          }
        }
        .latest-direct, .latest-urgent { background: ${colors.danger}; }
        .latest-from { font-weight: 800; font-size: 13px; }
        .latest-body { font-size: 14px; margin-top: 2px; }
        .latest-read {
          margin-top: 6px;
          background: rgba(23,22,28,0.3);
          color: ${colors.bg};
          border: none;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 12px;
        }
        .reply-banner {
          position: absolute;
          bottom: calc(env(safe-area-inset-bottom) + 170px);
          left: 12px; right: 12px;
          background: ${colors.danger};
          color: ${colors.bg};
          border: none;
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 13px;
          font-weight: 600;
          z-index: 1400;
          text-align: center;
          cursor: pointer;
        }
        .toast {
          position: absolute;
          top: calc(env(safe-area-inset-top) + 140px);
          left: 12px; right: 12px;
          padding: 12px 14px;
          background: ${colors.surface};
          border-left: 3px solid ${colors.accent};
          border-radius: 8px;
          color: ${colors.text};
          font-size: 14px;
          z-index: 1100;
          line-height: 1.4;
        }
      `}</style>
    </div>
  );
}
